<script lang="ts">
  import { onMount } from "svelte";
  import { storyblokEditable } from "@storyblok/svelte";
  import { deviceDetails } from "./stores/deviceDetails";
  import AutoplayVideo from "./AutoplayVideo.svelte";
  import gsap from "gsap";

  // Get the block data
  export let blok;

  // Get the device details
  $: ({ isMobile, isTablet, isDesktop } = $deviceDetails);

  // Declare element references
  let section: HTMLElement;
  let mainHeading: HTMLElement;
  let slideNavigation: HTMLElement;
  let slideTexts: HTMLElement[] = [];
  let slideVideos: HTMLElement[] = [];
  let progressBars: HTMLElement[] = [];
  let gsapContext: gsap.Context;

  // Set initial state
  let activeNavIndex = 0;
  let activeVideoIndex = -1;
  let isMounted = false;

  // Starts the scroll pinning animation
  function startAnimation() {
    // Pin the slide navigation when the section is in the viewport
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        pin: slideNavigation,
        pinSpacing: false,
        anticipatePin: 0.3,
        start: "top top+=50px",
        end: "bottom top+=310px",
      },
    });

    // Set the active indexes when the video enters/leaves the viewport
    slideVideos.forEach((slideVideo, index) => {
      tl.to(slideVideo, {
        scrollTrigger: {
          trigger: slideVideo,
          start: "top 40%",
          end: "bottom 40%",
          onEnter: () => {
            activeNavIndex = index;
            activeVideoIndex = index;
          },
          onEnterBack: () => {
            activeNavIndex = index;
            activeVideoIndex = index;
          },
          onLeave: () => {
            if (activeVideoIndex === index) {
              activeVideoIndex = -1;
            }
          },
          onLeaveBack: () => {
            if (activeVideoIndex === index) {
              activeVideoIndex = -1;
            }
          },
        },
      });
    });
  }

  onMount(() => {
    // Show the section only once the component has mounted
    isMounted = true;

    if (isDesktop) {
      // Initialize animations for desktop
      gsapContext = gsap.context(() => {
        startAnimation();
      });
    }

    // Cleanup and/or reinitialize animations on window resize
    function handleWindowResize() {
      if (isMobile) {
        // Cleanup animations if changing to mobile viewport
        if (gsapContext) {
          gsapContext.revert();
        }
      } else {
        // Reinitialize animations when changing to desktop viewport
        if (gsapContext) gsapContext.revert();
        gsapContext = gsap.context(() => {
          startAnimation();
        });
      }
    }
    window.addEventListener("resize", handleWindowResize);

    // Unmount
    return () => {
      // Cleanup animations
      if (gsapContext) {
        gsapContext.revert();
      }
    };
  });
</script>

<section
  class="video-scroller-section section-header-width mx-auto"
  class:mobile={isMobile || isTablet}
  class:desktop={isDesktop}
  class:visible={isMounted}
  bind:this={section}
  use:storyblokEditable={blok}
>
  {#if isMobile || isTablet}
    <!-- Mobile -->
    <h1 class="main-heading" bind:this={mainHeading}>{blok.heading}</h1>
    <div class="slides">
      {#each blok.videoSlides as slide, i}
        <div class="slide">
          <div class="slide-video">
            <AutoplayVideo
              videoMobile={slide.videoMobile}
              videoDesktop={slide.videoDesktop}
              videoPlaceholderMobile={slide.videoPlaceholder}
              videoPlaceholderDesktop={slide.videoPlaceholder}
              restartOnScroll={true}
            />
          </div>
          <div class="slide-text" bind:this={slideTexts[i]}>
            <h2 class="slide-heading">{slide.heading}</h2>
            <h3 class="slide-subheading">{slide.subheading}</h3>
          </div>
        </div>
      {/each}
    </div>
  {:else}
    <!-- Desktop -->
    <div class="slides">
      <div class="slide-navigation-wrapper" bind:this={slideNavigation}>
        <h1 class="main-heading" bind:this={mainHeading}>{blok.heading}</h1>
        <div class="slide-navigation">
          <div class="slide-progress-bar">
            {#each blok.videoSlides as slide, i}
              <div
                class="slide-progress-bar"
                class:active={i === activeNavIndex}
                bind:this={progressBars[i]}
              ></div>
            {/each}
          </div>
          <div class="slide-text-wrapper">
            {#each blok.videoSlides as slide, i}
              <div
                class="slide-text"
                class:visible={i === activeNavIndex}
                bind:this={slideTexts[i]}
              >
                <h2 class="slide-heading">{slide.heading}</h2>
                <h3 class="slide-subheading">{slide.subheading}</h3>
              </div>
            {/each}
          </div>
        </div>
      </div>
      <div class="slide-video-wrapper">
        {#each blok.videoSlides as slide, i}
          <div class="slide-video" class:active={i === activeVideoIndex} bind:this={slideVideos[i]}>
            <AutoplayVideo
              videoMobile={slide.videoMobile}
              videoDesktop={slide.videoDesktop}
              videoPlaceholderMobile={slide.videoPlaceholder}
              videoPlaceholderDesktop={slide.videoPlaceholder}
              restartOnScroll={true}
            />
          </div>
        {/each}
      </div>
    </div>
  {/if}
</section>

<style lang="postcss">
  .video-scroller-section {
    opacity: 0;
    margin-top: 6rem;
    margin-bottom: 6rem;

    &.visible {
      opacity: 1;
    }
  }

  .main-heading {
    font-size: 38px;
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    line-height: 1.1;
    color: #fff;
    white-space: pre-wrap;
  }

  .slide {
    margin-top: 80px;

    &:first-child {
      margin-top: 60px;
    }
  }

  .slide-text {
    margin-top: 1.25rem;
  }

  .slide-video {
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    border: 2px solid #379ccd;
    border-radius: 10px;
    overflow: hidden;
  }

  .slide-heading {
    font-size: 24px;
    font-family: var(--font-primary);
    font-weight: var(--font-weight-medium);
    line-height: 1.6;
    color: #fff;
  }

  .slide-subheading {
    width: 90%;
    margin-top: 10px;
    font-size: 16px;
    font-weight: 300;
    color: #fff;
  }

  /* Desktop Only */

  .video-scroller-section.desktop {
    margin-top: 6rem;
    margin-bottom: 6rem;

    @screen lg {
      margin-top: 0;
    }

    .main-heading {
      margin-bottom: 3rem;
      font-size: 48px;
    }

    .slides {
      display: grid;
      grid-template-columns: 40% 1fr;
      column-gap: 1rem;

      @media (min-width: 1500px) {
        grid-template-columns: 37% 1fr;
      }

      @media (min-width: 1200px) and (max-height: 800px) {
        grid-template-columns: 44% 1fr;
      }
    }

    .slide-heading {
      font-size: 24px;
      white-space: nowrap;
    }

    .slide-subheading {
      width: 100%;
      font-size: 18px;
    }

    .slide-text {
      position: absolute;
      margin-top: 0;
      max-width: 370px;
      transform: translatex(-10px);
      transition: transform 0.3s ease;
      pointer-events: none;
      opacity: 0;

      &.visible {
        transform: translatex(0);
        pointer-events: auto;
        opacity: 1;
      }
    }

    .slide-video {
      margin-bottom: 6rem;
      border: 2px solid rgba(84, 179, 223, 0.3);
      transition:
        border-color 0.2s ease,
        filter 0.2s ease,
        box-shadow 0.2s ease;

      &.active {
        border-color: rgba(84, 179, 223, 0.8);
        box-shadow: 0px 0px 10px 0px #54b3df;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    .slide-navigation {
      display: grid;
      grid-template-columns: 5% 95%;
      margin-left: 3px;
    }

    .slide-progress-bar {
      width: 2px;
      height: 10px;
      margin-bottom: 8px;
      border-radius: 2px;
      background-color: #54b3df;
      transition:
        background-color 0.3s,
        height 0.3s;

      &.active {
        height: 35px;
        background-color: #fff;
      }
    }
  }
</style>
